import React from 'react'
import styled from 'styled-components'

import { BuyMeCallout, PageTitle } from 'components/content'
import { Box, Container, DefaultLayout, Row } from 'components/layout'
import { HappituGuidePrice, HappituVisionPrice } from 'components/pricing/price'
import ProductCard from 'components/pricing/product-card'
import SignUpButton from 'components/pricing/sign-up-button'
import Spacer from 'components/spacer'
import { SubHeading, HeadingGroup, Paragraph } from 'components/typography'
import guideBackground from 'images/guide-background.svg'
import guideIcon from 'images/guide-icon.png'
import visionIcon from 'images/vision-icon.png'
import getStyle from 'themes/getStyle'

interface Props {
  className?: string
}

// interface FAQItemProps {
//   question: ReactNode
//   answer: ReactNode
// }

// const FAQItem = (props: FAQItemProps) => {
//   return (
//     <Row>
//       <div>
//         <SubHeading>{props.question}</SubHeading>
//         <SubHeading as="p">
//           <Light style={{ fontWeight: 'normal' }}>{props.answer}</Light>
//         </SubHeading>
//       </div>
//     </Row>
//   )
// }

const Table = styled.table`
  border: 1px solid ${getStyle('siteBorderColorDark')};
  border-collapse: collapse;
  margin: 2em auto;

  td {
    padding: 1em;
    border: 1px solid ${getStyle('siteBorderColorDark')};
    width: 50%;
  }
`

const ProductionSection = styled.section`
  display: grid;
  grid-template-rows: min-content 1fr;
  align-items: stretch;
  justify-content: stretch;
`

const ProductionContainer = styled.div`
  width: 100%;
  margin: 1em auto;
  display: grid;

  grid-gap: 2em;
  max-width: 1200px;

  @media screen and (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
`

const ProductIcon = styled.div`
  background: url(${(props) => props.src}) no-repeat center;
  background-size: contain;
  height: 150px;
  width: 100%;
`

const PricingPage = (props: Props) => {
  return (
    <DefaultLayout title="Pricing" slug="/pricing/">
      <div className={props.className}>
        <PageTitle title={<>Deliver remarkable customer experiences with Happitu.</>} />

        <Box fill="alt">
          <ProductionContainer>
            <ProductionSection>
              <HeadingGroup justify="center">
                <ProductIcon src={guideIcon} />
                <h2>Happitu Guide</h2>
                <Paragraph>Agent scripting & contextual knowledge base</Paragraph>
              </HeadingGroup>

              <ProductCard
                // Event snippet for Guide Lead Form conversion
                conversionTrackingId="AW-499257182/_yO3CK7Q1rIDEN6eiO4B"
                conversionValue={25}
                eventLabel="guide demo"
                title="Happitu Guide"
                description="Agent scripting & contextual knowledge base"
                src={guideIcon}
                price={<HappituGuidePrice />}
                formId="ec7eb3d3-4cd8-43c9-9b49-e7aef9b80f32"
                featureRoute="/agent-scripting/"
                background={guideBackground}
                includes={[
                  { title: '1 user' },
                  { title: '2,500 self-service interactions' },
                ]}
                features={[
                  {
                    title: 'Representative support',
                    children: [
                      { title: 'Responsive scripting' },
                      { title: 'Decision tree' },
                      { title: 'Consistent ticket documentation' },
                      { title: 'Role-based permissions' },
                      { title: 'Workflow management' },
                      { title: 'Outcome reporting' },
                      { title: 'Automated escalations' },
                      { title: 'Contextual knowledge base' },
                    ],
                  },
                  {
                    title: 'Self-service support',
                    children: [
                      { title: 'Contextual knowledge articles' },
                      { title: 'Step-by-step troubleshooting' },
                      { title: 'Automated escalations' },
                      { title: 'Embeds anywhere' },
                    ],
                  },
                ]}
              />
            </ProductionSection>
            <ProductionSection>
              <HeadingGroup justify="center">
                <ProductIcon src={visionIcon} />
                <h2>Happitu Vision</h2>
                <Paragraph>AI conversation analysis & quality management</Paragraph>
              </HeadingGroup>

              <ProductCard
                // Event snippet for Vision Lead Form conversion
                conversionTrackingId="AW-499257182/toNRCKvQ1rIDEN6eiO4B"
                conversionValue={95}
                eventLabel="vision demo"
                title="Happitu Vision"
                description="AI conversation analysis & quality management"
                src={visionIcon}
                featureRoute="/conversation-intelligence/"
                formId="9c783374-a4ff-4bc2-b611-eb68ba20d263"
                price={<HappituVisionPrice />}
                includes={[
                  { title: '5 local numbers' },
                  { title: '1000 transcription minutes' },
                ]}
                features={[
                  {
                    title: 'Service quality analytics',
                    children: [
                      { title: 'Call transcription' },
                      { title: 'Customer sentiment analysis' },
                      { title: 'Call driver identification' },
                      { title: 'Call recording storage' },
                    ],
                  },
                  {
                    title: 'Personalized coaching',
                    children: [
                      { title: 'Event segmentation' },
                      { title: 'Monitor process adherence' },
                      { title: 'Soft-skill analysis' },
                    ],
                  },
                ]}
              />
            </ProductionSection>
          </ProductionContainer>
        </Box>

        <Spacer y="3" />

        <Container>
          <Row cols="1">
            <Box fill="alt" rounded>
              <SubHeading bold>Need more than one solution?</SubHeading>
              <Paragraph>
                We would be happy to help assembled what your company needs.
              </Paragraph>
            </Box>
          </Row>

          <Row cols="1">
            <Box fill="alt" rounded>
              <SubHeading bold>Have a lot of volume?</SubHeading>
              <Paragraph>
                Happitu was founded by contact center veterans and was built designed to
                scale with you.
              </Paragraph>
              <Table>
                <tr>
                  <td>Built for contact centers</td>
                  <td>Volume discounting</td>
                </tr>
                <tr>
                  <td>Seamlessly sync with existing telephony solution</td>
                  <td>Additional call event metrics</td>
                </tr>
              </Table>
            </Box>
          </Row>
        </Container>
        <Container>
          <BuyMeCallout
            // Event snippet for General Lead Form conversion
            conversionTrackingId="AW-499257182/LiOXCLHQ1rIDEN6eiO4B"
            eventLabel="learn more"
            conversionValue={95}
            label="Learn More"
            heading="Reach out to find out more"
          />
        </Container>
      </div>
    </DefaultLayout>
  )
}

export default styled(PricingPage)`
  > section:first-of-type {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 4rem;
    z-index: 2;

    @media screen and (min-width: 768px) {
      margin-bottom: 6rem;
    }
  }

  ${SignUpButton} {
    button {
      width: 100%;
    }
  }

  @media screen and (min-width: 425px) {
    ${SignUpButton} {
      button {
        width: fit-content;
      }
    }
  }
`
